import { SKELETON_BONES, HEAD_KEYPOINTS } from './skeletonDefs';

const STYLE = {
  normalColor: '#99FC23',
  highlightColor: '#FF4D4F',
  lineWidth: 6,
  pointRadius: 5,
  headRadius: 10,
  pointHitRadius: 20,
  lineHitDistance: 16,
};

const MIN_DISPLAY_SIZE = 100;
const MAX_DISPLAY_SIZE = 300;

const isValid = (kp, threshold = 0.1) => kp && (kp.s ?? kp.score) >= threshold;
const getColor = (hl) => hl ? STYLE.highlightColor : STYLE.normalColor;

const DISABLED_BONES = new Set([
  'LEFT_SHOULDER-LEFT_HIP',
  'RIGHT_SHOULDER-RIGHT_HIP',
]);

const getMidpoint = (kp1, kp2) => {
  const valid1 = isValid(kp1);
  const valid2 = isValid(kp2);
  if (valid1 && valid2) return { x: (kp1.x + kp2.x) / 2, y: (kp1.y + kp2.y) / 2 };
  if (valid1) return { x: kp1.x, y: kp1.y };
  if (valid2) return { x: kp2.x, y: kp2.y };
  return null;
};

const drawLine = (ctx, from, to, color) => {
  if (!from || !to) return;
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
  ctx.closePath();
};

const buildKpHighlightMap = (highlightedItems) => {
  const map = {};
  highlightedItems.forEach((id) => {
    if (id.startsWith('kp:')) {
      map[parseInt(id.substring(3))] = true;
    } else if (id === 'head') {
      map[HEAD_KEYPOINTS.leftEar] = true;
      map[HEAD_KEYPOINTS.rightEar] = true;
      map[HEAD_KEYPOINTS.nose] = true;
    } else if (id.startsWith('bone:')) {
      const boneName = id.substring(5);
      if (boneName === 'HEAD-NECK') {
        map[HEAD_KEYPOINTS.leftEar] = true;
        map[HEAD_KEYPOINTS.rightEar] = true;
        map[HEAD_KEYPOINTS.nose] = true;
        map[5] = true;
        map[6] = true;
      } else if (boneName === 'NECK-HIP') {
        map[5] = true; map[6] = true; map[11] = true; map[12] = true;
      } else if (!DISABLED_BONES.has(boneName)) {
        const bone = SKELETON_BONES.find((b) => b[2] === boneName);
        if (bone) {
          map[bone[0]] = true;
          map[bone[1]] = true;
        }
      }
    }
  });
  return map;
};

export const drawSkeleton = (
  ctx,
  keypoints,
  highlightedItems = new Set(),
  scale = 1,
  displaySize = 0
) => {
  if (!ctx || !keypoints || keypoints.length < 17) return;

  const pose = keypoints;
  const isBoneHL = (id) => highlightedItems.has(id);
  const kpHLMap = buildKpHighlightMap(highlightedItems);
  const isKpHL = (idx) => !!kpHLMap[idx];

  let visualFactor = 1;
  if (displaySize > 0 && displaySize < MAX_DISPLAY_SIZE) {
    const t = Math.max(0, (displaySize - MIN_DISPLAY_SIZE) / (MAX_DISPLAY_SIZE - MIN_DISPLAY_SIZE));
    visualFactor = 0.15 + 0.85 * t;
  }

  const inv = 1 / scale;
  const lineWidth = STYLE.lineWidth * inv * visualFactor;
  const pointRadius = STYLE.pointRadius * inv * visualFactor;
  const headRadius = STYLE.headRadius * inv * visualFactor;

  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';

  const leftEar = pose[HEAD_KEYPOINTS.leftEar];
  const rightEar = pose[HEAD_KEYPOINTS.rightEar];
  const nose = pose[HEAD_KEYPOINTS.nose];
  let headCenter = null;
  if (isValid(leftEar) && isValid(rightEar)) {
    headCenter = { x: (leftEar.x + rightEar.x) / 2, y: (leftEar.y + rightEar.y) / 2 };
  } else if (isValid(nose)) {
    headCenter = { x: nose.x, y: nose.y - 10 };
  }

  const leftShoulder = pose[5];
  const rightShoulder = pose[6];
  const leftHip = pose[11];
  const rightHip = pose[12];
  const neck = getMidpoint(leftShoulder, rightShoulder);
  const hip = getMidpoint(leftHip, rightHip);

  if (headCenter && neck) drawLine(ctx, headCenter, neck, getColor(isBoneHL('bone:HEAD-NECK')));
  drawLine(ctx, neck, hip, getColor(isBoneHL('bone:NECK-HIP')));
  if (isValid(leftShoulder) && isValid(rightShoulder)) {
    drawLine(ctx, leftShoulder, rightShoulder, getColor(isBoneHL('bone:LEFT_SHOULDER-RIGHT_SHOULDER')));
  }
  if (isValid(leftHip) && isValid(rightHip)) {
    drawLine(ctx, leftHip, rightHip, getColor(isBoneHL('bone:LEFT_HIP-RIGHT_HIP')));
  }

  const limbBones = [
    [5, 7, 'LEFT_SHOULDER-LEFT_ELBOW'],
    [7, 9, 'LEFT_ELBOW-LEFT_WRIST'],
    [6, 8, 'RIGHT_SHOULDER-RIGHT_ELBOW'],
    [8, 10, 'RIGHT_ELBOW-RIGHT_WRIST'],
    [11, 13, 'LEFT_HIP-LEFT_KNEE'],
    [13, 15, 'LEFT_KNEE-LEFT_ANKLE'],
    [12, 14, 'RIGHT_HIP-RIGHT_KNEE'],
    [14, 16, 'RIGHT_KNEE-RIGHT_ANKLE'],
  ];

  limbBones.forEach(([from, to, boneName]) => {
    const fromKp = pose[from];
    const toKp = pose[to];
    if (!isValid(fromKp) || !isValid(toKp)) return;
    const hl = isBoneHL(`bone:${boneName}`);
    ctx.beginPath();
    ctx.strokeStyle = getColor(hl);
    ctx.moveTo(fromKp.x, fromKp.y);
    ctx.lineTo(toKp.x, toKp.y);
    ctx.stroke();
    ctx.closePath();
  });

  const skipIndices = [0, 1, 2, 3, 4];
  for (let i = 0; i < 17; i++) {
    if (skipIndices.includes(i)) continue;
    const kp = pose[i];
    if (!isValid(kp)) continue;
    const hl = isKpHL(i);
    ctx.beginPath();
    ctx.strokeStyle = getColor(hl);
    ctx.fillStyle = getColor(hl);
    ctx.lineWidth = 2 * inv * visualFactor;
    ctx.arc(kp.x, kp.y, pointRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.closePath();
  }

  if (headCenter) {
    ctx.beginPath();
    ctx.fillStyle = getColor(isBoneHL('head'));
    ctx.strokeStyle = getColor(isBoneHL('head'));
    ctx.lineWidth = lineWidth;
    ctx.arc(headCenter.x, headCenter.y, headRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.closePath();
  }
};

const getHeadCenter = (keypoints) => {
  const leftEar = keypoints[HEAD_KEYPOINTS.leftEar];
  const rightEar = keypoints[HEAD_KEYPOINTS.rightEar];
  const nose = keypoints[HEAD_KEYPOINTS.nose];
  if (isValid(leftEar) && isValid(rightEar)) {
    return { x: (leftEar.x + rightEar.x) / 2, y: (leftEar.y + rightEar.y) / 2 };
  }
  if (isValid(nose)) return { x: nose.x, y: nose.y - 10 };
  return null;
};

export const hitTestKeypoint = (x, y, keypoints, scale = 1) => {
  if (!keypoints) return null;
  const hitR = STYLE.pointHitRadius / scale;
  const headCenter = getHeadCenter(keypoints);
  if (headCenter) {
    const headHitRadius = STYLE.headRadius / scale + 4 / scale;
    const d = Math.hypot(x - headCenter.x, y - headCenter.y);
    if (d <= headHitRadius) return 'head';
  }
  const skipIndices = [0, 1, 2, 3, 4];
  for (let i = 0; i < keypoints.length; i++) {
    if (skipIndices.includes(i)) continue;
    const kp = keypoints[i];
    if (!isValid(kp)) continue;
    if (Math.hypot(x - kp.x, y - kp.y) <= hitR) return `kp:${i}`;
  }
  return null;
};

const ptSegDist = (px, py, x1, y1, x2, y2) => {
  const dx = x2 - x1, dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
};

export const hitTestBone = (x, y, keypoints) => {
  if (!keypoints) return null;
  const hitDist = STYLE.lineHitDistance;
  const leftEar = keypoints[HEAD_KEYPOINTS.leftEar];
  const rightEar = keypoints[HEAD_KEYPOINTS.rightEar];
  const nose = keypoints[HEAD_KEYPOINTS.nose];
  let headCenter = null;
  if (isValid(leftEar) && isValid(rightEar)) {
    headCenter = { x: (leftEar.x + rightEar.x) / 2, y: (leftEar.y + rightEar.y) / 2 };
  } else if (isValid(nose)) {
    headCenter = { x: nose.x, y: nose.y - 10 };
  }
  const leftShoulder = keypoints[5];
  const rightShoulder = keypoints[6];
  const leftHip = keypoints[11];
  const rightHip = keypoints[12];
  const neck = getMidpoint(leftShoulder, rightShoulder);
  const hip = getMidpoint(leftHip, rightHip);
  if (headCenter && neck && ptSegDist(x, y, headCenter.x, headCenter.y, neck.x, neck.y) <= hitDist) {
    return 'bone:HEAD-NECK';
  }
  if (neck && hip && ptSegDist(x, y, neck.x, neck.y, hip.x, hip.y) <= hitDist) return 'bone:NECK-HIP';
  if (isValid(leftShoulder) && isValid(rightShoulder) && ptSegDist(x, y, leftShoulder.x, leftShoulder.y, rightShoulder.x, rightShoulder.y) <= hitDist) {
    return 'bone:LEFT_SHOULDER-RIGHT_SHOULDER';
  }
  if (isValid(leftHip) && isValid(rightHip) && ptSegDist(x, y, leftHip.x, leftHip.y, rightHip.x, rightHip.y) <= hitDist) {
    return 'bone:LEFT_HIP-RIGHT_HIP';
  }
  for (const [from, to, boneName] of SKELETON_BONES) {
    if (DISABLED_BONES.has(boneName)) continue;
    const fromKp = keypoints[from];
    const toKp = keypoints[to];
    if (!isValid(fromKp) || !isValid(toKp)) continue;
    if (ptSegDist(x, y, fromKp.x, fromKp.y, toKp.x, toKp.y) <= hitDist) {
      return `bone:${boneName}`;
    }
  }
  return null;
};
